import { useEffect, useRef } from "react";
import type { FC } from "react";
import { Menu, MenuItem } from "react-aria-components";
import { cx } from "@/utils/cx";

export interface ContextMenuItem {
    id: string;
    label: string;
    icon?: FC<{ className?: string }>;
    onAction: () => void;
    isDisabled?: boolean;
    isDestructive?: boolean;
}

interface ContextMenuProps {
    isOpen: boolean;
    onClose: () => void;
    position: { x: number; y: number } | null;
    items: ContextMenuItem[];
}

export const ContextMenu = ({ isOpen, onClose, position, items }: ContextMenuProps) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen || !position) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, position, onClose]);

    if (!isOpen || !position) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 z-40"
                onClick={onClose}
            />
            {/* Menu */}
            <div
                ref={menuRef}
                className="fixed z-50 w-62 rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt overflow-auto"
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                }}
            >
                <Menu
                    disallowEmptySelection
                    selectionMode="single"
                    onAction={(key) => {
                        const item = items.find(i => i.id === key as string);
                        if (item && !item.isDisabled) {
                            item.onAction();
                            onClose();
                        }
                    }}
                    className="py-1 outline-hidden select-none"
                >
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <MenuItem
                                key={item.id}
                                id={item.id}
                                isDisabled={item.isDisabled}
                                className={(state) =>
                                    cx(
                                        "group block cursor-pointer px-1.5 py-px outline-hidden",
                                        state.isDisabled && "cursor-not-allowed",
                                    )
                                }
                            >
                                {(state) => (
                                    <div
                                        className={cx(
                                            "relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear",
                                            !state.isDisabled && "group-hover:bg-primary_hover",
                                            state.isFocused && "bg-primary_hover",
                                            state.isFocusVisible && "outline-2 -outline-offset-2",
                                            item.isDestructive && !state.isDisabled && "text-error-primary",
                                        )}
                                    >
                                        {Icon && (
                                            <Icon
                                                aria-hidden="true"
                                                className={cx(
                                                    "mr-2 size-4 shrink-0 stroke-[2.25px]",
                                                    state.isDisabled
                                                        ? "text-fg-disabled"
                                                        : item.isDestructive
                                                            ? "text-error-primary"
                                                            : "text-fg-quaternary"
                                                )}
                                            />
                                        )}
                                        <span
                                            className={cx(
                                                "grow truncate text-sm font-semibold",
                                                state.isDisabled
                                                    ? "text-disabled"
                                                    : item.isDestructive
                                                        ? "text-error-primary"
                                                        : "text-secondary",
                                                state.isFocused && "text-secondary_hover",
                                            )}
                                        >
                                            {item.label}
                                        </span>
                                    </div>
                                )}
                            </MenuItem>
                        );
                    })}
                </Menu>
            </div>
        </>
    );
};

